import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	forwardRef,
	inject,
	input,
	model,
	NgZone,
	OnDestroy,
	output,
	signal,
	ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ClickOutsideDirective } from '../select-dropdown/click-outside.directive';

// Interface for selected items with input values
export interface SelectedItemWithValue {
	option: any;
	inputValue?: string | number;
}

@Component({
	selector: 'input-dropdown',
	imports: [CommonModule, ClickOutsideDirective],
	templateUrl: './input-dropdown.component.html',
	styleUrl: './input-dropdown.component.css',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => InputDropdownComponent),
			multi: true
		}
	]
})
export class InputDropdownComponent
	implements ControlValueAccessor, AfterViewInit, OnDestroy
{
	// Input signals
	options = input<any[]>([]);
	label = input<string>('');
	placeholder = input<string>('Select options');
	minWidth = input<string>('100px');
	width = input<string | null>(null);
	searchPlaceholder = input<string>('Search options...');
	enableSearch = input<boolean>(true);
	dropPosition = input<'bottom' | 'top'>('bottom');

	// NEW: Input field configuration
	enableInputFields = input<boolean>(false);
	inputPlaceholder = input<string>('Enter value');
	inputType = input<'text' | 'number'>('text');
	inputWidth = input<string>('100px');

	// Custom class inputs
	containerClass = input<string>('');
	dropdownClass = input<string>('');
	optionClass = input<string>('');

	// Computed signals for merged classes
	mergedContainerClass = computed(() => {
		const baseClasses =
			'select-container w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none cursor-pointer bg-white dark:bg-gray-700 dark:border-gray-600 relative';
		return this.containerClass()
			? `${baseClasses} ${this.containerClass()}`
			: baseClasses;
	});

	mergedDropdownClass = computed(() => {
		const baseClasses =
			'absolute z-10 w-full bg-white dark:bg-slate-800 shadow-lg rounded-md text-base ring-1 ring-black dark:ring-slate-600 ring-opacity-5 focus:outline-none max-h-60 overflow-auto flex flex-col';
		return this.dropdownClass()
			? `${baseClasses} ${this.dropdownClass()}`
			: baseClasses;
	});

	mergedOptionClass = computed(() => {
		const baseClasses =
			'px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer text-gray-900 dark:text-gray-100';
		return this.optionClass()
			? `${baseClasses} ${this.optionClass()}`
			: baseClasses;
	});

	required = input<any>(false);

	isRequiredState = computed(() => {
		const requiredValue = this.required();
		if (requiredValue === undefined || requiredValue === null) {
			return false;
		}
		if (typeof requiredValue === 'string') {
			return requiredValue !== 'false' && requiredValue !== '';
		}
		return Boolean(requiredValue);
	});

	// Output emitter - now emits with input values if enabled
	selectionChange = output<any[]>();

	// Model signal - can be either simple array or array with input values
	selectedValues = model<any[]>([]);

	// Internal state signals
	isOpen = signal<boolean>(false);
	selectAllChecked = signal<boolean>(false);
	indeterminate = signal<boolean>(false);
	searchTerm = signal<string>('');

	// NEW: Store input values for each selected option
	inputValues = signal<Map<string, string | number>>(new Map());

	dropdownPosition = signal<'bottom' | 'top'>(this.dropPosition());

	@ViewChild('selectContainer') selectContainer!: ElementRef;
	@ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

	filteredOptions = computed(() => {
		const options = this.options();
		const term = this.searchTerm().toLowerCase().trim();

		if (!term || !this.enableSearch()) {
			return options;
		}

		return options.filter((option) => {
			const displayText = this.getOptionDisplay(option).toLowerCase();
			return displayText.includes(term);
		});
	});

	filteredSelectAllChecked = computed(() => {
		const filtered = this.filteredOptions();
		if (filtered.length === 0) return false;
		return filtered.every((option) => this.isSelected(option));
	});

	filteredIndeterminate = computed(() => {
		const filtered = this.filteredOptions();
		if (filtered.length === 0) return false;

		const selectedInFiltered = filtered.filter((option) =>
			this.isSelected(option)
		);
		return (
			selectedInFiltered.length > 0 &&
			selectedInFiltered.length < filtered.length
		);
	});

	private readonly elementRef = inject(ElementRef);
	private readonly ngZone = inject(NgZone);
	private resizeListener: any;

	private onChange = (value: any) => {};
	private onTouched = () => {};

	constructor() {
		effect(() => {
			const currentOptions = this.options();
			if (
				currentOptions?.length === 0 &&
				this.selectedValues()?.length > 0
			) {
				this.resetSelection();
			}
			this.updateSelectAllState();
		});
	}

	ngAfterViewInit(): void {
		this.ngZone.runOutsideAngular(() => {
			this.resizeListener = () => {
				if (this.isOpen()) {
					this.updateDropdownPosition();
				}
			};
			window.addEventListener('resize', this.resizeListener);
			window.addEventListener('scroll', this.resizeListener, true);
		});
	}

	ngOnDestroy(): void {
		if (this.resizeListener) {
			window.removeEventListener('resize', this.resizeListener);
			window.removeEventListener('scroll', this.resizeListener, true);
		}
	}

	writeValue(value: any[]): void {
		if (this.enableInputFields() && value && value.length > 0) {
			// Check if value contains objects with inputValue
			if (typeof value[0] === 'object' && 'option' in value[0]) {
				const items = value as SelectedItemWithValue[];
				const options = items.map((item) => item.option);
				const inputMap = new Map<string, string | number>();

				for (const item of items) {
					const key = this.getOptionKey(item.option);
					if (item.inputValue !== undefined) {
						inputMap.set(key, item.inputValue);
					}
				}

				this.selectedValues.set(options);
				this.inputValues.set(inputMap);
			} else {
				this.selectedValues.set(value || []);
			}
		} else {
			this.selectedValues.set(value || []);
		}

		this.updateSelectAllState();
		this.emitValue();
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	toggleDropdown(): void {
		this.isOpen.update((state) => !state);
		if (this.isOpen()) {
			this.onTouched();
			this.updateDropdownPosition();
			setTimeout(() => {
				if (this.enableSearch() && this.searchInput) {
					this.searchInput.nativeElement.focus();
				}
			}, 0);
		} else {
			this.searchTerm.set('');
		}
	}

	closeDropdown(): void {
		this.isOpen.set(false);
		this.searchTerm.set('');
	}

	onSearchInput(event: Event): void {
		const target = event.target as HTMLInputElement;
		this.searchTerm.set(target.value);
	}

	clearSearch(): void {
		this.searchTerm.set('');
		if (this.searchInput) {
			this.searchInput.nativeElement.focus();
		}
	}

	updateDropdownPosition(): void {
		if (!this.selectContainer) return;

		const containerRect =
			this.selectContainer.nativeElement.getBoundingClientRect();
		const baseDropdownHeight = this.enableSearch() ? 100 : 50;
		const dropdownHeight = Math.min(
			300,
			this.filteredOptions().length * 40 + baseDropdownHeight
		);
		const viewportHeight = window.innerHeight;

		const spaceBelow = viewportHeight - containerRect.bottom;
		const spaceAbove = containerRect.top;

		if (
			this.dropPosition() === 'bottom' &&
			(spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove)
		) {
			this.dropdownPosition.set('bottom');
		} else {
			this.dropdownPosition.set('top');
		}
	}

	resetSelection(): void {
		this.selectedValues.set([]);
		this.inputValues.set(new Map());
		this.selectAllChecked.set(false);
		this.indeterminate.set(false);
		this.searchTerm.set('');
		this.emitValue();
	}

	isObjectOption(option: any): boolean {
		return (
			typeof option === 'object' &&
			option !== null &&
			'value' in option &&
			'label' in option
		);
	}

	hasObjectOptions(): boolean {
		const opts = this.options();
		return opts.length > 0 && this.isObjectOption(opts[0]);
	}

	getOptionDisplay(option: any): string {
		return this.isObjectOption(option) ? option.label : option;
	}

	getOptionValue(option: any): any {
		return this.isObjectOption(option) ? option.value : option;
	}

	getOptionKey(option: any): string {
		return JSON.stringify(this.getOptionValue(option));
	}

	getSelectionDisplayText(): string {
		const selected = this.selectedValues();

		if (selected.length === 0) {
			return this.placeholder();
		} else if (selected.length === 1) {
			return this.getOptionDisplay(selected[0]);
		} else {
			return selected.length + ' selected';
		}
	}

	toggleOption(option: any, event: Event): void {
		event.preventDefault();
		const checkbox = event.target as HTMLInputElement;

		const currentValues = [...this.selectedValues()];
		const optionIndex = currentValues.findIndex(
			(item) => this.getOptionValue(item) === this.getOptionValue(option)
		);

		if (optionIndex === -1) {
			currentValues.push(option);
		} else {
			currentValues.splice(optionIndex, 1);
			// Remove input value when unchecking
			if (this.enableInputFields()) {
				const key = this.getOptionKey(option);
				const currentInputValues = new Map(this.inputValues());
				currentInputValues.delete(key);
				this.inputValues.set(currentInputValues);
			}
		}

		this.selectedValues.set(currentValues);
		this.updateSelectAllState();
		this.emitValue();

		checkbox.checked = this.isSelected(option);
	}

	toggleSelectAll(event: Event): void {
		event.preventDefault();
		const checkbox = event.target as HTMLInputElement;
		const filtered = this.filteredOptions();

		let newValues: any[];
		const currentValues = [...this.selectedValues()];

		if (this.filteredSelectAllChecked()) {
			newValues = currentValues.filter(
				(selected) =>
					!filtered.some(
						(filtered) =>
							this.getOptionValue(selected) ===
							this.getOptionValue(filtered)
					)
			);
			// Remove input values for deselected items
			if (this.enableInputFields()) {
				const currentInputValues = new Map(this.inputValues());
				for (const option of filtered) {
					currentInputValues.delete(this.getOptionKey(option));
				}
				this.inputValues.set(currentInputValues);
			}
		} else {
			const valuesToAdd = filtered.filter(
				(filtered) =>
					!currentValues.some(
						(selected) =>
							this.getOptionValue(selected) ===
							this.getOptionValue(filtered)
					)
			);
			newValues = [...currentValues, ...valuesToAdd];
		}

		this.selectedValues.set(newValues);
		this.updateSelectAllState();
		this.emitValue();

		checkbox.checked = this.filteredSelectAllChecked();
		checkbox.indeterminate = this.filteredIndeterminate();
	}

	updateSelectAllState(): void {
		const selectedValues = this.selectedValues();
		const options = this.options();
		const totalOptions = options?.length || 0;

		if (totalOptions === 0) {
			this.selectAllChecked.set(false);
			this.indeterminate.set(false);
			return;
		}

		if (selectedValues?.length === 0) {
			this.selectAllChecked.set(false);
			this.indeterminate.set(false);
		} else if (selectedValues.length === totalOptions) {
			this.selectAllChecked.set(true);
			this.indeterminate.set(false);
		} else {
			this.selectAllChecked.set(false);
			this.indeterminate.set(true);
		}
	}

	isSelected(option: any): boolean {
		const selectedValues = this.selectedValues();

		if (this.isObjectOption(option)) {
			return selectedValues.some(
				(item) =>
					this.isObjectOption(item) && item.value === option.value
			);
		} else {
			return selectedValues.includes(option);
		}
	}

	// NEW: Handle input value change
	onInputValueChange(option: any, event: Event): void {
		event.stopPropagation();
		const input = event.target as HTMLInputElement;
		const key = this.getOptionKey(option);
		const currentInputValues = new Map(this.inputValues());

		if (input.value) {
			const value =
				this.inputType() === 'number'
					? Number.parseFloat(input.value) || 0
					: input.value;
			currentInputValues.set(key, value);
		} else {
			currentInputValues.delete(key);
		}

		this.inputValues.set(currentInputValues);
		this.emitValue();
	}

	// NEW: Get input value for an option
	getInputValue(option: any): string | number {
		const key = this.getOptionKey(option);
		return this.inputValues().get(key) || '';
	}

	// NEW: Check if input should be readonly
	isInputReadonly(option: any): boolean {
		return !this.isSelected(option);
	}

	// NEW: Emit value with input values if enabled
	private emitValue(): void {
		const selected = this.selectedValues();

		if (this.enableInputFields()) {
			const result: SelectedItemWithValue[] = selected.map((option) => ({
				option,
				inputValue: this.inputValues().get(this.getOptionKey(option))
			}));
			this.onChange(result);
			this.selectionChange.emit(result);
		} else {
			this.onChange(selected);
			this.selectionChange.emit(selected);
		}
	}
}
